// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook for fetching inventory data for return order dialogs.
 * This module provides functionality for fetching all inventory at a single location
 * including non-inventory (customer) locations, and groups products by functional status.
 */
import { useAssetsInventory } from '@/inventory/hooks/use-assets-inventory';
import { useItemInventory } from '@/inventory/hooks/use-item-inventory';
import {
  buildLocationInventoryMap,
  toStockedBulkItems,
} from '@/inventory/utilities/build-location-inventory-map';
import { toSiteId } from '@/location/utilities/to-site-id';
import { SparseLocation, SparseLocationWithParent } from '@/sparse/api/types';
import { useMemo } from 'react';

/**
 * Configuration options for the useReturnOrderInventory hook.
 */
interface UseReturnOrderInventoryOptions {
  location: SparseLocation | SparseLocationWithParent | null | undefined;
}

/**
 * Return type for the useReturnOrderInventory hook.
 */
interface UseReturnOrderInventoryResult {
  /** Map structure: locationId -> itemId -> quantity (for both parts and products), keyed by each exact site or zone */
  inventoryMap: Map<string, Map<string, number>>;
  /** True if any inventory query is loading for the first time */
  isLoading: boolean;
  /** True if any inventory query is fetching (including background refetches) */
  isFetching: boolean;
}

/**
 * A React hook that fetches the inventory at a location's site for use in return order forms.
 *
 * Key differences from useShipmentInventory:
 * - Includes non-inventory (customer) locations via includeNonInventory: true
 * - Sums ALL asset functional statuses into a single count (not just FUNCTIONAL)
 *
 * Every zone of a site shares one cached query, and the map is keyed by each exact site or zone.
 *
 * @param options - Configuration object with the selected location
 * @returns Object containing inventory map, loading states
 *
 * @example
 * ```tsx
 * const { inventoryMap, isLoading } = useReturnOrderInventory({
 *   location: fromLocation
 * });
 *
 * // Look up available quantity for a part or product
 * const availableQty = inventoryMap.get(locationId)?.get(itemId) ?? 0;
 * ```
 */
export function useReturnOrderInventory(
  options: UseReturnOrderInventoryOptions
): UseReturnOrderInventoryResult {
  const siteId = options.location ? toSiteId(options.location) : undefined;

  const itemInventory = useItemInventory({
    filters: {
      locationIds: siteId ? [siteId] : undefined,
      includeNonInventory: true,
    },
    enabled: !!siteId,
  });

  const assetsInventory = useAssetsInventory({
    filters: {
      locationId: siteId ? [siteId] : undefined,
      includeNonInventory: true,
    },
    enabled: !!siteId,
  });

  const inventoryMap = useMemo(
    () =>
      buildLocationInventoryMap([
        ...toStockedBulkItems(itemInventory.itemInventory?.data.results ?? []),
        ...(assetsInventory.assetsInventory?.data.results ?? []),
      ]),
    [itemInventory.itemInventory, assetsInventory.assetsInventory]
  );

  return {
    inventoryMap,
    isLoading: itemInventory.isLoading || assetsInventory.isLoading,
    isFetching: itemInventory.isFetching || assetsInventory.isFetching,
  };
}
